import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, tap, of } from 'rxjs';
import { environment } from '../../../environments/environment';

const cache = new Map<string, { response: HttpResponse<any>, expiry: number, timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes for API responses
const MAX_CACHE_SIZE = 100; // Maximum number of cached items

// LRU (Least Recently Used) cache management
const cacheAccessOrder: string[] = [];

const log = (message: string, ...args: any[]) => {
  if (!environment.production) {
    //console.log(message, ...args);
  }
};

const evictOldestCacheEntry = () => {
  if (cacheAccessOrder.length > 0) {
    const oldestKey = cacheAccessOrder.shift();
    if (oldestKey) {
      cache.delete(oldestKey);
      log('🗑️ Evicted oldest cache entry:', oldestKey);
    }
  }
};

const updateCacheAccess = (key: string) => {
  const index = cacheAccessOrder.indexOf(key);
  if (index > -1) {
    cacheAccessOrder.splice(index, 1);
  }
  cacheAccessOrder.push(key);
};

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests to API endpoints
  if (req.method !== 'GET' || !req.url.includes('/api/ih/')) {
    return next(req);
  }

  // Skip caching for real-time or user-specific data
  if (req.url.includes('/auth/') || req.url.includes('/payment/create') || req.url.includes('/payment/premium')) {
    return next(req);
  }

  // Check cache
  const cached = cache.get(req.url);
  if (cached && cached.expiry > Date.now()) {
    log('🚀 Cache hit:', req.url);
    updateCacheAccess(req.url);
    return of(cached.response.clone());
  }

  // If cache expired, remove it
  if (cached) {
    cache.delete(req.url);
    const index = cacheAccessOrder.indexOf(req.url);
    if (index > -1) {
      cacheAccessOrder.splice(index, 1);
    }
  }

  // Make request and cache response
  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        // Check cache size limit
        if (cache.size >= MAX_CACHE_SIZE) {
          evictOldestCacheEntry();
        }

        log('💾 Caching response:', req.url);
        const now = Date.now();
        cache.set(req.url, {
          response: event.clone(),
          expiry: now + CACHE_DURATION,
          timestamp: now
        });
        updateCacheAccess(req.url);
      }
    })
  );
};

/**
 * Clear cache for specific URL or all cache
 * Used for cache invalidation on user actions like logout
 */
export const clearCache = (url?: string): void => {
  if (url) {
    cache.delete(url);
    const index = cacheAccessOrder.indexOf(url);
    if (index > -1) {
      cacheAccessOrder.splice(index, 1);
    }
    log('🗑️ Cache cleared for URL:', url);
  } else {
    cache.clear();
    cacheAccessOrder.length = 0;
    log('🗑️ All cache cleared');
  }
};

/**
 * Get cache size for debugging
 */
export const getCacheSize = (): number => {
  return cache.size;
};

/**
 * Get cache statistics for monitoring
 */
export const getCacheStats = () => {
  const now = Date.now();
  const entries = Array.from(cache.entries());
  
  return {
    size: cache.size,
    maxSize: MAX_CACHE_SIZE,
    entries: entries.map(([url, data]) => ({
      url,
      age: Math.floor((now - data.timestamp) / 1000),
      expiresIn: Math.floor((data.expiry - now) / 1000)
    }))
  };
};